import { useEffect, useRef, useState } from "react";
import { Canvas } from "@react-three/fiber";
import { motion, useMotionValueEvent, useScroll } from "framer-motion";
import HouseScene from "@/components/three/HouseScene";

const STAGES = ["SITE PREPARATION", "FOUNDATION", "STRUCTURAL FRAMING", "EXTERIOR WALLS", "MATERIALS & GLASS", "INTERIOR LIGHT", "FINAL DETAILS", "WELCOME HOME"];

const lineVariants = {
  hidden: { y: "115%" },
  visible: (i) => ({
    y: "0%",
    transition: { delay: 0.35 + i * 0.14, duration: 1.05, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function Hero() {
  const ref = useRef(null);
  const progress = useRef(0);
  const [stage, setStage] = useState(0);
  const [fallback, setFallback] = useState(false);

  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });

  useMotionValueEvent(scrollYProgress, "change", (v) => {
    progress.current = v;
    const s = Math.min(STAGES.length - 1, Math.floor(v * STAGES.length));
    setStage((prev) => (prev === s ? prev : s));
  });

  useEffect(() => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const mobile = window.matchMedia("(max-width: 767px)").matches;
    let webgl = true;
    try {
      webgl = !!document.createElement("canvas").getContext("webgl");
    } catch (e) {
      webgl = false;
    }
    setFallback(reduced || mobile || !webgl);
  }, []);

  const scrollTo = (sel) => {
    const el = document.querySelector(sel);
    if (!el) return;
    if (window.__lenis) window.__lenis.scrollTo(el, { offset: -70 });
    else el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="home" ref={ref} data-testid="hero-section" className="relative h-[520vh]">
      <div className="sticky top-0 h-screen overflow-hidden">
        {fallback ? (
          <div className="absolute inset-0">
            <img
              src="/images/hero-real-2.webp"
              alt="Entrance of a luxury Texas home at dusk with warm interior lights glowing"
              className="w-full h-full object-cover"
              loading="eager"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-ink/70 via-ink/30 to-ink" />
          </div>
        ) : (
          <Canvas
            shadows
            dpr={[1, 1.75]}
            camera={{ position: [20, 11, 21], fov: 38 }}
            gl={{ antialias: true, alpha: true }}
            data-testid="hero-3d-canvas"
          >
            <fog attach="fog" args={["#0D0D0E", 30, 70]} />
            <HouseScene progress={progress} />
          </Canvas>
        )}

        {/* architectural frame lines */}
        <div className="pointer-events-none absolute inset-0 hidden lg:block">
          <div className="absolute left-12 top-0 bottom-0 w-px bg-goldw/10" />
          <div className="absolute right-12 top-0 bottom-0 w-px bg-goldw/10" />
        </div>

        {/* overlay content */}
        <motion.div
          className="absolute inset-0 flex items-center"
          style={{ opacity: stage > 0 ? 0 : 1 }}
        >
          <div className="px-6 lg:px-24 max-w-[1600px] mx-auto w-full pointer-events-none">
            <div className="max-w-3xl">
              <motion.p
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.8 }}
                className="font-mono text-[10px] sm:text-xs tracking-[0.35em] text-goldw uppercase mb-6"
                data-testid="hero-eyebrow"
              >
                Premium Construction &amp; Remodeling
              </motion.p>
              <h1 className="font-serif font-light text-ivory text-4xl sm:text-6xl lg:text-[5.5rem] leading-[1.02] tracking-tight" data-testid="hero-headline">
                <span className="mask-line">
                  <motion.span custom={0} variants={lineVariants} initial="hidden" animate="visible" className="block">
                    WE BUILD
                  </motion.span>
                </span>
                <span className="mask-line">
                  <motion.span custom={1} variants={lineVariants} initial="hidden" animate="visible" className="block">
                    LUXURY <span className="italic text-goldw">SPACES</span>
                  </motion.span>
                </span>
              </h1>
              <motion.p
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.9, duration: 0.9 }}
                className="mt-7 text-sm sm:text-base text-zinc2 leading-relaxed max-w-xl"
                data-testid="hero-copy"
              >
                From custom-built homes to transformative kitchens, bathrooms and complete
                renovations, AMAA Constructions brings premium craftsmanship to homes across
                Dallas–Fort Worth.
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.1, duration: 0.9 }}
                className="mt-10 flex flex-wrap gap-4 pointer-events-auto"
              >
                <button
                  data-testid="hero-capabilities-btn"
                  onClick={() => scrollTo("#capabilities")}
                  className="group relative overflow-hidden bg-ivory text-ink font-mono text-[11px] tracking-[0.22em] uppercase px-8 py-4 transition-colors duration-300"
                >
                  <span className="relative z-10 transition-colors duration-300 group-hover:text-ivory">View Our Capabilities</span>
                  <span className="absolute inset-0 bg-goldd translate-y-full group-hover:translate-y-0 transition-transform duration-300" />
                </button>
                <button
                  data-testid="hero-quote-btn"
                  onClick={() => scrollTo("#contact")}
                  className="border border-ivory/40 text-ivory font-mono text-[11px] tracking-[0.22em] uppercase px-8 py-4 hover:border-goldw hover:text-goldw transition-colors duration-300"
                >
                  Get a Free Quote
                </button>
              </motion.div>
            </div>
          </div>
        </motion.div>

        {/* stage label */}
        <div className="absolute bottom-10 left-6 lg:left-24 flex items-center gap-4" data-testid="hero-stage-label">
          <span className="font-mono text-[10px] tracking-[0.3em] text-goldw">{String(stage + 1).padStart(2, "0")}</span>
          <span className="h-px w-10 bg-goldw/40" />
          <span className="font-mono text-[10px] tracking-[0.3em] text-zinc2 uppercase">{STAGES[stage]}</span>
        </div>

        {/* scroll indicator */}
        <div className="absolute bottom-10 right-6 lg:right-24 flex flex-col items-center gap-3" data-testid="hero-scroll-indicator">
          <span className="font-mono text-[9px] tracking-[0.3em] text-zinc3 uppercase rotate-90 origin-center translate-y-[-8px]">Scroll</span>
          <div className="w-px h-14 bg-ivory/15 overflow-hidden mt-4">
            <div className="w-full h-full bg-goldw animate-scroll-pulse" />
          </div>
        </div>
      </div>
    </section>
  );
}
